import {inject, Injectable} from '@angular/core';
import {LogLevel} from "../log-level.enum";
import {LOG_LEVEL, LOGGER_PROVIDERS} from "../../../injection-tokens";
import {LoggerServiceInterface} from "./logger.service.interface";
import {LogPublisher} from "../logger-providers/log-publisher.interface";

/**
 * Delegates logging actions to the logging providers.
 */
@Injectable({
  providedIn: 'root'
})
export class LoggerService implements LoggerServiceInterface {

  // Log level specified in the env provider
  private logLevel = inject(LOG_LEVEL) ?? LogLevel.NEVER;

  // Logger providers specified in the env provider - eg. console logger, http logger, file logger
  private loggerProviders: LogPublisher[] = inject(LOGGER_PROVIDERS) ?? [];

  /**
   * Creates a formatted message for the log.
   *
   * @param message - Log message
   * @param logLevel - Log level (eg. INFO, WARN, ERROR)
   * @private
   */
  private format(message: string, logLevel: string): string {
    return `${new Date()} | ${logLevel}: ${message}`
  }

  /**
   * Returns whether a log should be published based on log level.
   * @param level - the level of the log
   * @private
   */
  private canLog(level: LogLevel) {
    return this.logLevel <= level;
  }

  /**
   * Invokes the debug method of each logging provider if the correct logging level is set.
   *
   * @param message - Log message
   * @param optionalParams - Optional params to include in log
   */
  debug(message: string, ...optionalParams: any[]): void {
    if (this.canLog(LogLevel.DEBUG)) {
      const formattedMessage = this.format(message, 'DEBUG');
      this.loggerProviders.forEach(provider => provider.debug(formattedMessage, optionalParams));
    }
  }

  /**
   * Invokes the info method of each logging provider if the correct logging level is set.
   *
   * @param message - Log message
   * @param optionalParams - Optional params to include in log
   */
  info(message: string, ...optionalParams: any[]): void {
    if (this.canLog(LogLevel.INFO)) {
      const formattedMessage = this.format(message, 'INFO');
      this.loggerProviders.forEach(provider => provider.info(formattedMessage, optionalParams));
    }
  }

  /**
   * Invokes the warn method of each logging provider if the correct logging level is set.
   *
   * @param message - Log message
   * @param optionalParams - Optional params to include in log
   */
  warn(message: string, ...optionalParams: any[]): void {
    if (this.canLog(LogLevel.WARNING)) {
      const formattedMessage = this.format(message, 'WARNING');
      this.loggerProviders.forEach(provider => provider.warn(formattedMessage, optionalParams));
    }
  }

  /**
   * Invokes the error method of each logging provider if the correct logging level is set.
   *
   * @param message - Log message
   * @param optionalParams - Optional params to include in log
   */
  error(message: string, ...optionalParams: any[]): void {
    if (this.canLog(LogLevel.ERROR)) {
      const formattedMessage = this.format(message, 'ERROR');
      this.loggerProviders.forEach(provider => provider.error(formattedMessage, optionalParams));
    }
  }
}
